/**
 * Generic cookie utilities for client-side cookie management.
 */

export interface CookieOptions {
  maxAgeDays?: number; // Default: session cookie
  path?: string; // Default: '/'
  secure?: boolean; // Default: true in production
  sameSite?: 'Strict' | 'Lax' | 'None'; // Default: 'Lax'
}

const isProduction = import.meta.env.VITE_ENV === 'production';

/**
 * Set a cookie with the given name, value, and options.
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const { maxAgeDays, path = '/', secure = isProduction, sameSite = 'Lax' } = options;

  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookie += `; path=${path}`;
  cookie += `; SameSite=${sameSite}`;

  if (secure) {
    cookie += '; Secure';
  }

  if (maxAgeDays !== undefined) {
    const maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
    cookie += `; max-age=${maxAgeSeconds}`;
  }

  document.cookie = cookie;
}

/**
 * Get a cookie value by name.
 * Returns null if the cookie doesn't exist.
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  const encodedName = encodeURIComponent(name);

  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    const separatorIndex = trimmed.indexOf('=');
    if (separatorIndex === -1) continue;
    const cookieName = trimmed.substring(0, separatorIndex);
    const cookieValue = trimmed.substring(separatorIndex + 1);
    if (cookieName === encodedName) {
      return decodeURIComponent(cookieValue || '');
    }
  }

  return null;
}

/**
 * Delete a cookie by setting its expiration to the past.
 */
export function deleteCookie(name: string, path = '/'): void {
  let cookie = `${encodeURIComponent(name)}=; path=${path}; max-age=0; SameSite=Lax`;
  if (isProduction) {
    cookie += '; Secure';
  }
  document.cookie = cookie;
}
